import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import type { Buttons } from "../../../../types/Buttons";
import ButtonModal from './ButtonModal';
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';

const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

const ButtonsPage: React.FC = () => {
  const [buttons, setButtons] = useState<Buttons[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<Buttons | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});

  const cancel: PopconfirmProps['onCancel'] = (e) => {
    console.log(e);
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchButtons = async () => {
    try {
      const data = await apiRequest<Buttons[]>({
        method: 'get',
        url: 'buttons',
      });
      setButtons(data);
    } catch (error) {
      showError('Failed to fetch buttons');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchButtons();
  }, []);

  const handleEditClick = (record: Buttons) => {
    setIsEditMode(true);
    setInitialValues({
      button_id: record.button_id,
      button_name: record.button_name,
      button_status: record.button_status,
      button_image: record.button_image,
    });
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchButtons();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (button_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-button/${button_id}`,
      });
      showSuccess('Button deleted');
      fetchButtons();
    } catch {
      showError('Failed to delete button');
    }
  };

  const handleStatusToggle = async (buttonId: number) => {
    setButtons((prev) =>
      prev.map((item) =>
        item.button_id === buttonId
          ? { ...item, button_status: item.button_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-button/${buttonId}`,
      });
      showSuccess('Button status updated');
    } catch (error) {
      showError('Failed to update status');
      setButtons((prev) =>
        prev.map((item) =>
          item.button_id === buttonId
            ? { ...item, button_status: item.button_status === 1 ? 0 : 1 }
            : item
        )
      );
    }
  };

  const handleTableChange: TableProps<Buttons>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };

  const columns: TableColumnsType<Buttons> = [
    {
      title: 'Button',
      dataIndex: 'button_name',
      key: 'button_name',
      width: 500,
      filters: Array.from(new Set(buttons.map((s) => s.button_name))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.button_name || null,
      onFilter: (value, record) => record.button_name.includes(value as string),
      sorter: (a, b) => a.button_name.localeCompare(b.button_name),
      sortOrder: sortedInfo.columnKey === 'button_name' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.button_name}</div>,
    },
    {
      title: 'Image',
      dataIndex: 'button_image',
      key: 'button_image',
      width: 500,
      render: (_: any, record: any) =>
        record ? (
          <img
            src={record.button_image}
            alt="button"
            style={{
              width: 40,
              height: 50,
              objectFit: 'cover',
              borderRadius: 8,
            }}
          />
        ) : (
          '-'
        ),
    },
  ];

  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 200,
      render: (_: any, record: Buttons) => (
        <Space size="middle">
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={!!record.button_status}
                onChange={() => handleStatusToggle(record.button_id)}
              />
              <Button onClick={() => handleEditClick(record)} className="px-8">
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this button?"
              onConfirm={() => handleDelete(record.button_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger className="px-8">
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }

  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Button</Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === 'Y' && (
          <Flex gap="small">
            <Button type="primary" onClick={showCreateModal}>
              Add Button
            </Button>
          </Flex>
        )}
      </Space>

      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<Buttons>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="button_id"
          dataSource={buttons.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          scroll={{ y: 37 * 13 }}
          locale={{ emptyText: 'No button found' }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–{' '}
                {Math.min(pagination.current * pagination.pageSize, buttons.length)} of{' '}
                {buttons.length}
              </span>
              <Pagination
                {...pagination}
                total={buttons.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />
      </div>

      <ButtonModal
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
        buttons={buttons}
      />
    </>
  );
};

export default ButtonsPage;
